import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import LoadingSpinner from '../ui/LoadingSpinner';
import { callAdmin } from '@/integrations/supabase/client';

interface VoiceUser {
  id: string;
  email: string;
  name: string;
  voice_tokens_used: number;
  voice_conversations_count: number;
  last_voice_conversation_at: string;
}

interface VoiceConversation {
  created_at: string;
  voice_tokens_used: number;
  voice_duration_seconds: number;
}

interface VoiceAnalytics {
  total_voice_users: number;
  total_voice_tokens_used: number;
  total_voice_conversations: number;
  avg_tokens_per_user: number;
  voice_conversations_count: number;
  avg_voice_duration: number;
  voice_messages_count: number;
  top_voice_users: VoiceUser[];
  voice_conversations_timeline: VoiceConversation[];
}

export const VoiceAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<VoiceAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchVoiceAnalytics();
  }, []);

  const fetchVoiceAnalytics = async () => {
    try {
      setLoading(true);
      const result = await callAdmin('get_voice_analytics', {});
      
      if (result.error) {
        setError(result.error);
      } else {
        setAnalytics(result.data as VoiceAnalytics);
      }
    } catch (err) {
      setError('Failed to fetch voice analytics');
      console.error('Error fetching voice analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return '0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center p-8 text-gray-500">
        No voice analytics available
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Voice Analytics</h2>
          <p className="text-gray-600">Track voice feature usage and statistics</p>
        </div>
        <Button onClick={fetchVoiceAnalytics} variant="outline">
          🔄 Refresh
        </Button>
      </div>

      {/* Overall Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{analytics.total_voice_users || 0}</div>
            <div className="text-sm text-gray-600">Total Voice Users</div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {(analytics.total_voice_tokens_used || 0).toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Total Tokens Used</div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {analytics.voice_conversations_count || 0}
            </div>
            <div className="text-sm text-gray-600">Voice Conversations</div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {formatDuration(Math.round(analytics.avg_voice_duration || 0))}
            </div>
            <div className="text-sm text-gray-600">Avg Duration</div>
          </div>
        </Card>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage Metrics</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Avg Tokens per User</span>
              <span className="font-semibold text-gray-900">
                {Math.round(analytics.avg_tokens_per_user || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Voice Messages</span>
              <span className="font-semibold text-gray-900">
                {(analytics.voice_messages_count || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Conversations</span>
              <span className="font-semibold text-gray-900">
                {(analytics.total_voice_conversations || 0).toLocaleString()}
              </span>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            <div className="flex items-center text-sm">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
              <span className="text-gray-600">
                {analytics.voice_conversations_timeline?.filter(c => {
                  const date = new Date(c.created_at);
                  const today = new Date();
                  return date.toDateString() === today.toDateString();
                }).length || 0} voice conversations today
              </span>
            </div>
            <div className="flex items-center text-sm">
              <span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>
              <span className="text-gray-600">
                {analytics.voice_conversations_timeline?.length || 0} conversations (last 30 days)
              </span>
            </div>
            <div className="flex items-center text-sm">
              <span className="w-2 h-2 bg-purple-400 rounded-full mr-3"></span>
              <span className="text-gray-600">
                {analytics.top_voice_users?.length || 0} active voice users
              </span>
            </div>
          </div>
        </Card>
      </div>

      {/* Top Voice Users */}
      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Top Voice Users</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tokens Used
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conversations
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Activity
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(analytics.top_voice_users || []).map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {user.name || 'No name'}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {(user.voice_tokens_used || 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.voice_conversations_count || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(user.last_voice_conversation_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!analytics.top_voice_users || analytics.top_voice_users.length === 0) && (
            <div className="text-center py-8 text-gray-500">
              No voice users yet
            </div>
          )}
        </div>
      </Card>

      {/* Usage Timeline */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Voice Usage Timeline (Last 30 Days)</h3>
        {analytics.voice_conversations_timeline && analytics.voice_conversations_timeline.length > 0 ? (
          <div className="space-y-2">
            <div className="grid grid-cols-7 gap-2 text-xs text-gray-500 mb-2">
              <div>Date</div>
              <div className="col-span-2">Conversations</div>
              <div className="col-span-2">Tokens</div>
              <div className="col-span-2">Duration</div>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {analytics.voice_conversations_timeline.slice(0, 10).map((conv, index) => (
                <div key={index} className="grid grid-cols-7 gap-2 py-2 text-sm border-t border-gray-100">
                  <div className="text-gray-600">
                    {new Date(conv.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                  <div className="col-span-2 text-gray-900">1 conversation</div>
                  <div className="col-span-2 text-gray-900">{conv.voice_tokens_used.toLocaleString()} tokens</div>
                  <div className="col-span-2 text-gray-900">{formatDuration(conv.voice_duration_seconds)}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No voice conversations in the last 30 days
          </div>
        )}
      </Card>

      {/* Voice Feature Status */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <span className="text-2xl">🎤</span>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-900">Voice Feature Status</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>The voice feature is currently active. Users can engage with Sage using voice commands.</p>
              <p className="mt-2">
                To disable the voice feature site-wide, go to <strong>Settings</strong> and toggle{' '}
                <strong>voice_feature_enabled</strong> to false.
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

